import {
	Component,
	EventEmitter,
	Output,
	signal,
	computed,
	inject,
	OnInit,
	OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
	BookingService,
	CalendarAvailability,
	TimeSlot,
	ConsultationService,
	OtpVerifyResponse,
} from '../../services/booking.service';

type BookingStep = 'email' | 'otp' | 'details' | 'datetime' | 'confirm' | 'success';

// Step configuration for progress sidebar
interface StepConfig {
	id: BookingStep;
	label: string;
	description: string;
}

@Component({
	selector: 'app-booking-modal',
	standalone: true,
	imports: [CommonModule, FormsModule],
	templateUrl: './booking-modal.html',
	styleUrl: './booking-modal.css',
})
export class BookingModal implements OnInit, OnDestroy {
	@Output() close = new EventEmitter<void>();

	private bookingService = inject(BookingService);

	// Steps configuration for progress sidebar (4 main steps)
	stepsList: StepConfig[] = [
		{ id: 'email', label: 'Verify Email', description: 'Email & OTP verification' },
		{ id: 'details', label: 'Your Details', description: 'Contact information' },
		{ id: 'datetime', label: 'Date & Time', description: 'Pick appointment slot' },
		{ id: 'confirm', label: 'Confirm', description: 'Review & book' },
	];

	// Current step
	currentStep = signal<BookingStep>('email');

	// Loading states
	isLoading = signal(false);
	isSendingOtp = signal(false);
	isVerifyingOtp = signal(false);
	isBooking = signal(false);

	// Error messages
	errorMessage = signal('');

	// Email step
	email = signal('');

	// OTP step
	otp = signal('');
	otpDigits = signal<string[]>(['', '', '', '', '', '']);
	canResendOtp = signal(true);
	resendCountdown = signal(0);
	private resendTimer: any;

	// Customer data (from OTP verification)
	token = signal('');
	isExistingCustomer = signal(false);
	existingCustomer = signal<OtpVerifyResponse['customer'] | null>(null);

	// Details step
	firstName = signal('');
	lastName = signal('');
	phone = signal('');

	// Calendar step
	calendarAvailability = signal<CalendarAvailability[]>([]);
	selectedDate = signal<string | null>(null);
	currentMonth = signal(new Date());

	// Time slot step
	timeSlots = signal<TimeSlot[]>([]);
	selectedTimeSlot = signal<TimeSlot | null>(null);

	// Service step
	services = signal<ConsultationService[]>([]);
	selectedService = signal<ConsultationService | null>(null);

	// Notes
	notes = signal('');

	// Consultation mode (online/offline)
	consultationMode = signal<'offline' | 'online'>('offline');

	// Booking result
	bookingReference = signal('');

	// Computed values
	calendarWeeks = computed(() => {
		const availability = this.calendarAvailability();
		const weeks: CalendarAvailability[][] = [];
		for (let i = 0; i < availability.length; i += 7) {
			weeks.push(availability.slice(i, i + 7));
		}
		return weeks;
	});

	currentMonthLabel = computed(() => {
		const date = this.currentMonth();
		return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
	});

	isEmailValid = computed(() => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(this.email());
	});

	isOtpComplete = computed(() => {
		return this.otpDigits().every((d) => d.length === 1);
	});

	isDetailsValid = computed(() => {
		return (
			this.firstName().trim().length > 0 &&
			this.lastName().trim().length > 0 &&
			this.phone().trim().length >= 10
		);
	});

	// Check if datetime step is complete
	isDateTimeValid = computed(() => {
		return this.selectedDate() !== null && this.selectedTimeSlot() !== null;
	});

	// Check if confirm step is valid (notes are optional)
	isConfirmValid = computed(() => {
		return true; // Notes are no longer required
	});

	ngOnInit() {
		document.body.style.overflow = 'hidden';
		this.loadServices();
	}

	ngOnDestroy() {
		document.body.style.overflow = '';
		if (this.resendTimer) {
			clearInterval(this.resendTimer);
		}
	}

	// Get step index for progress tracking
	getStepIndex(stepId: BookingStep): number {
		// Map internal steps to display steps
		if (stepId === 'email' || stepId === 'otp') return 0;
		if (stepId === 'details') return 1;
		if (stepId === 'datetime') return 2;
		if (stepId === 'confirm') return 3;
		return 4; // success
	}

	// Get visible steps for progress (excluding success)
	getVisibleSteps(): StepConfig[] {
		// For existing customers, skip the details step
		if (this.isExistingCustomer()) {
			return this.stepsList.filter((s) => s.id !== 'details');
		}
		return this.stepsList;
	}

	// Format short date for summary
	formatShortDate(dateStr: string): string {
		const date = new Date(dateStr + 'T00:00:00');
		return date.toLocaleDateString('en-US', {
			weekday: 'short',
			month: 'short',
			day: 'numeric',
		});
	}

	// Get time slots by time of day
	getMorningSlots(): TimeSlot[] {
		return this.timeSlots().filter((slot) => {
			const hour = parseInt(slot.start.split(':')[0]);
			return hour < 12;
		});
	}

	getAfternoonSlots(): TimeSlot[] {
		return this.timeSlots().filter((slot) => {
			const hour = parseInt(slot.start.split(':')[0]);
			return hour >= 12 && hour < 17;
		});
	}

	getEveningSlots(): TimeSlot[] {
		return this.timeSlots().filter((slot) => {
			const hour = parseInt(slot.start.split(':')[0]);
			return hour >= 17;
		});
	}

	// Book another appointment (reset modal)
	bookAnother() {
		this.currentStep.set('email');
		this.email.set('');
		this.otp.set('');
		this.otpDigits.set(['', '', '', '', '', '']);
		this.firstName.set('');
		this.lastName.set('');
		this.phone.set('');
		this.selectedDate.set(null);
		this.selectedTimeSlot.set(null);
		this.selectedService.set(null);
		this.notes.set('');
		this.consultationMode.set('offline');
		this.bookingReference.set('');
		this.errorMessage.set('');
		this.token.set('');
		this.isExistingCustomer.set(false);
		this.existingCustomer.set(null);
		this.currentMonth.set(new Date());
		this.timeSlots.set([]);
	}

	closeModal() {
		this.close.emit();
	}

	// Email step
	sendOtp() {
		if (!this.isEmailValid()) return;

		this.isSendingOtp.set(true);
		this.errorMessage.set('');

		this.bookingService.sendOtp(this.email()).subscribe({
			next: (response) => {
				this.isSendingOtp.set(false);
				if (response.status) {
					this.currentStep.set('otp');
					this.startResendCountdown();
				} else {
					this.errorMessage.set(response.message || 'Failed to send OTP');
				}
			},
			error: (error) => {
				this.isSendingOtp.set(false);
				this.errorMessage.set(
					error.error?.message || 'Failed to send OTP. Please try again.',
				);
			},
		});
	}

	// OTP step
	onOtpInput(index: number, event: Event) {
		const input = event.target as HTMLInputElement;
		const value = input.value.replace(/\D/g, '');

		const digits = [...this.otpDigits()];
		digits[index] = value.slice(0, 1);
		this.otpDigits.set(digits);

		// Auto-focus next input
		if (value && index < 5) {
			const nextInput = document.querySelector(`#otp-${index + 1}`) as HTMLInputElement;
			nextInput?.focus();
		}

		// Update combined OTP
		this.otp.set(digits.join(''));
	}

	onOtpKeydown(index: number, event: KeyboardEvent) {
		if (event.key === 'Backspace' && !this.otpDigits()[index] && index > 0) {
			const prevInput = document.querySelector(`#otp-${index - 1}`) as HTMLInputElement;
			prevInput?.focus();
		}
	}

	onOtpPaste(event: ClipboardEvent) {
		event.preventDefault();
		const pastedText = event.clipboardData?.getData('text') || '';
		const digits = pastedText.replace(/\D/g, '').slice(0, 6).split('');

		const newDigits = [...this.otpDigits()];
		digits.forEach((d, i) => {
			if (i < 6) newDigits[i] = d;
		});
		this.otpDigits.set(newDigits);
		this.otp.set(newDigits.join(''));
	}

	verifyOtp() {
		if (!this.isOtpComplete()) return;

		this.isVerifyingOtp.set(true);
		this.errorMessage.set('');

		this.bookingService.verifyOtp(this.email(), this.otp()).subscribe({
			next: (response) => {
				this.isVerifyingOtp.set(false);
				if (response.status) {
					this.token.set(response.data.token);
					this.isExistingCustomer.set(response.data.is_existing_customer);

					if (response.data.customer) {
						this.existingCustomer.set(response.data.customer);
						this.firstName.set(response.data.customer.first_name);
						this.lastName.set(response.data.customer.last_name);
						this.phone.set(response.data.customer.phone);
						// Skip details step for existing customers, go directly to datetime
						this.currentStep.set('datetime');
						this.loadCalendarData();
					} else {
						this.currentStep.set('details');
					}
				} else {
					this.errorMessage.set(response.message || 'Invalid OTP');
				}
			},
			error: (error) => {
				this.isVerifyingOtp.set(false);
				this.errorMessage.set(error.error?.message || 'Invalid or expired OTP');
			},
		});
	}

	resendOtp() {
		if (!this.canResendOtp()) return;

		this.isSendingOtp.set(true);
		this.bookingService.resendOtp(this.email()).subscribe({
			next: (response) => {
				this.isSendingOtp.set(false);
				if (response.status) {
					this.otpDigits.set(['', '', '', '', '', '']);
					this.otp.set('');
					this.startResendCountdown();
				}
			},
			error: (error) => {
				this.isSendingOtp.set(false);
				this.errorMessage.set(error.error?.message || 'Failed to resend OTP');
			},
		});
	}

	private startResendCountdown() {
		this.canResendOtp.set(false);
		this.resendCountdown.set(60);

		this.resendTimer = setInterval(() => {
			const current = this.resendCountdown();
			if (current <= 1) {
				this.canResendOtp.set(true);
				this.resendCountdown.set(0);
				clearInterval(this.resendTimer);
			} else {
				this.resendCountdown.set(current - 1);
			}
		}, 1000);
	}

	// Details step
	submitDetails() {
		if (!this.isDetailsValid()) return;

		this.currentStep.set('datetime');
		this.loadCalendarData();
	}

	// Calendar step
	loadCalendarData() {
		const currentDate = this.currentMonth();
		const year = currentDate.getFullYear();
		const month = currentDate.getMonth();

		// Get first and last day of the month
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month + 1, 0);

		const startDate = this.formatDate(firstDay);
		const endDate = this.formatDate(lastDay);

		this.isLoading.set(true);
		this.bookingService.getCalendarAvailability(startDate, endDate).subscribe({
			next: (response) => {
				this.isLoading.set(false);
				if (response.status) {
					// Generate full calendar grid with padding
					const calendarGrid = this.generateCalendarGrid(response.data);
					this.calendarAvailability.set(calendarGrid);
				}
			},
			error: () => {
				this.isLoading.set(false);
				this.errorMessage.set('Failed to load availability');
			},
		});
	}

	private generateCalendarGrid(availabilityData: CalendarAvailability[]): CalendarAvailability[] {
		const currentDate = this.currentMonth();
		const year = currentDate.getFullYear();
		const month = currentDate.getMonth();

		// Use local date construction consistently
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month + 1, 0);
		const startPadding = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.

		const grid: CalendarAvailability[] = [];

		// Today in local time
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		// Create a map of date to availability data for quick lookup
		const availabilityMap = new Map<string, CalendarAvailability>();
		availabilityData.forEach((day) => {
			availabilityMap.set(day.date, day);
		});

		// Previous month padding (grayed out)
		const prevMonthLastDay = new Date(year, month, 0).getDate();
		for (let i = startPadding - 1; i >= 0; i--) {
			const dayNum = prevMonthLastDay - i;
			const date = new Date(year, month - 1, dayNum);
			const dateStr = this.formatDateLocal(date);
			grid.push({
				date: dateStr,
				day_name: date.toLocaleDateString('en-US', { weekday: 'short' }),
				is_today: false,
				is_past: true,
				is_weekly_off: false,
				total_slots: 0,
				available_slots: 0,
				booked_slots: 0,
				availability_percentage: 0,
				status: 'gray',
			});
		}

		// Current month days
		for (let day = 1; day <= lastDay.getDate(); day++) {
			const date = new Date(year, month, day);
			const dateStr = this.formatDateLocal(date);
			const availData = availabilityMap.get(dateStr);

			if (availData) {
				grid.push(availData);
			} else {
				// If no data from API, mark as unavailable
				const dateTime = date.getTime();
				const todayTime = today.getTime();

				grid.push({
					date: dateStr,
					day_name: date.toLocaleDateString('en-US', { weekday: 'short' }),
					is_today: dateTime === todayTime,
					is_past: dateTime < todayTime,
					is_weekly_off: false,
					total_slots: 0,
					available_slots: 0,
					booked_slots: 0,
					availability_percentage: 0,
					status: 'gray',
				});
			}
		}

		// Next month padding to complete the grid (7 columns)
		const remainingSlots = (7 - (grid.length % 7)) % 7;
		for (let day = 1; day <= remainingSlots; day++) {
			const date = new Date(year, month + 1, day);
			const dateStr = this.formatDateLocal(date);
			grid.push({
				date: dateStr,
				day_name: date.toLocaleDateString('en-US', { weekday: 'short' }),
				is_today: false,
				is_past: false,
				is_weekly_off: false,
				total_slots: 0,
				available_slots: 0,
				booked_slots: 0,
				availability_percentage: 0,
				status: 'gray',
			});
		}

		return grid;
	}

	// Select date and load time slots (stays on same step)
	selectDate(day: CalendarAvailability) {
		if (day.status === 'gray' || day.is_past || day.is_weekly_off) return;

		this.selectedDate.set(day.date);
		this.selectedTimeSlot.set(null); // Reset time slot when date changes
		this.loadTimeSlots();
	}

	prevMonth() {
		const current = this.currentMonth();
		this.currentMonth.set(new Date(current.getFullYear(), current.getMonth() - 1, 1));
		this.loadCalendarData();
	}

	nextMonth() {
		const current = this.currentMonth();
		this.currentMonth.set(new Date(current.getFullYear(), current.getMonth() + 1, 1));
		this.loadCalendarData();
	}

	// Time slot step
	loadTimeSlots() {
		const date = this.selectedDate();
		if (!date) return;

		this.isLoading.set(true);
		this.bookingService.getTimeSlots(date).subscribe({
			next: (response) => {
				this.isLoading.set(false);
				if (response.status) {
					this.timeSlots.set(response.data.slots);
				}
			},
			error: () => {
				this.isLoading.set(false);
				this.errorMessage.set('Failed to load time slots');
			},
		});
	}

	// Select time slot (stays on same step)
	selectTimeSlot(slot: TimeSlot) {
		this.selectedTimeSlot.set(slot);
	}

	// Proceed from datetime to confirm step
	proceedToConfirm() {
		if (!this.isDateTimeValid()) return;
		this.currentStep.set('confirm');
	}

	// Service step
	loadServices() {
		this.bookingService.getConsultationServices().subscribe({
			next: (response) => {
				if (response.status) {
					this.services.set(response.data);
				}
			},
			error: () => {
				console.error('Failed to load services');
			},
		});
	}

	selectService(service: ConsultationService) {
		this.selectedService.set(service);
	}

	// Confirmation step
	confirmBooking() {
		const date = this.selectedDate();
		const slot = this.selectedTimeSlot();

		if (!date || !slot) return;

		this.isBooking.set(true);
		this.errorMessage.set('');

		this.bookingService
			.bookAppointment({
				token: this.token(),
				first_name: this.firstName(),
				last_name: this.lastName(),
				phone: this.phone(),
				appointment_date: date,
				start_time: slot.start,
				consultation_mode: this.consultationMode(),
				notes: this.notes(),
			})
			.subscribe({
				next: (response) => {
					this.isBooking.set(false);
					if (response.status) {
						this.bookingReference.set(response.data.booking_reference);
						this.currentStep.set('success');
					} else {
						this.errorMessage.set(response.message || 'Failed to book appointment');
					}
				},
				error: (error) => {
					this.isBooking.set(false);
					this.errorMessage.set(
						error.error?.message || 'Failed to book appointment. Please try again.',
					);
				},
			});
	}

	// Navigation
	goBack() {
		const step = this.currentStep();
		switch (step) {
			case 'otp':
				this.currentStep.set('email');
				break;
			case 'details':
				this.currentStep.set('email'); // Go back to email, will need to re-verify
				break;
			case 'datetime':
				if (this.isExistingCustomer()) {
					this.currentStep.set('email');
				} else {
					this.currentStep.set('details');
				}
				break;
			case 'confirm':
				this.currentStep.set('datetime');
				break;
		}
	}

	// Helpers
	private formatDate(date: Date): string {
		// Use local date to avoid UTC conversion issues
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	private formatDateLocal(date: Date): string {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	formatDisplayDate(dateStr: string): string {
		const date = new Date(dateStr + 'T00:00:00');
		return date.toLocaleDateString('en-US', {
			weekday: 'long',
			month: 'long',
			day: 'numeric',
			year: 'numeric',
		});
	}

	getStatusClass(status: string): string {
		switch (status) {
			case 'green':
				return 'bg-green-500';
			case 'orange':
				return 'bg-orange-500';
			default:
				return 'bg-gray-300';
		}
	}
}
